import React, { useState, useEffect } from "react";
import {
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  ShoppingCart,
  Heart,
  ShieldCheck,
  AlertTriangle,
  AlertCircle,
  PackageCheck,
} from "lucide-react";

const ProductDetailPage = ({
  productId,
  navigateTo,
  favorites,
  toggleFavorite,
  addToCart,
  productsData,
}) => {
  const product = productsData[productId];
  
  const [activeImage, useStateActiveImage] = useState(0);
  const [isPackageOpen, setIsPackageOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setIsPackageOpen(false);
  }, [productId]);

  if (!product)
    return (
      <div className="p-20 text-center">
        Produkt nenalezen.{" "}
        <button
          onClick={() => navigateTo("products")}
          className="text-green-600 underline font-bold"
        >
          Zpět na E-shop
        </button>
      </div>
    );

  const isFav = favorites?.includes(product.id);

  const productImages =
    product.images || (product.image ? [product.image] : []);

  const nextImage = () => {
    useStateActiveImage((prev) => (prev + 1) % productImages.length);
  };

  const prevImage = () => {
    useStateActiveImage((prev) =>
      prev === 0 ? productImages.length - 1 : prev - 1
    );
  };

  // Bezpečná kontrola, zda jde o displej EKD01 (hledáme v názvu "EKD01" bez ohledu na velikost písmen)
  const isEKD01 = product.name?.toUpperCase().includes("EKD01") || product.id?.toUpperCase().includes("EKD01");

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 md:py-12">
      <button
        onClick={() => navigateTo("products")}
        className="mb-8 flex items-center text-sm font-bold text-slate-500 hover:text-green-600 transition w-fit"
      >
        <ArrowRight className="w-4 h-4 mr-2 rotate-180" /> Zpět na E-shop
      </button>

      {/* DVOUSLOUPCOVÝ LAYOUT */}
      <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
        
        {/* LEVÝ SLOUPEC: Fotky, Text o produktu, Aplikace (jen EKD01), Legalita, Obsah balení */}
        <div className="lg:w-5/12 flex flex-col space-y-6">
          
          {/* Fotogalerie */}
          <div className="bg-[#f8f9fa] rounded-xl border border-slate-100 p-8 aspect-square flex items-center justify-center relative shadow-sm">
            {productImages.length > 1 && (
              <button
                onClick={prevImage}
                className="absolute left-4 p-2 bg-white/80 hover:bg-white rounded-md shadow-sm border border-slate-200 z-10"
              >
                <ChevronLeft className="w-5 h-5 text-slate-600" />
              </button>
            )}

            {productImages.length > 0 ? (
              <img
                src={productImages[activeImage]}
                alt={product.name}
                className="max-h-full max-w-full object-contain mix-blend-multiply"
              />
            ) : (
              <div className="text-slate-400 font-medium">
                Zatím bez obrázku
              </div>
            )}

            {productImages.length > 1 && (
              <button
                onClick={nextImage}
                className="absolute right-4 p-2 bg-white/80 hover:bg-white rounded-md shadow-sm border border-slate-200 z-10"
              >
                <ChevronRight className="w-5 h-5 text-slate-600" />
              </button>
            )}

            {productImages.length > 1 && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                {productImages.map((_, idx) => (
                  <div
                    key={idx}
                    className={`w-2 h-2 rounded-full ${
                      activeImage === idx ? "bg-green-600" : "bg-slate-300"
                    }`}
                  ></div>
                ))}
              </div>
            )}
          </div>

          {/* Náhledy fotek */}
          {productImages.length > 1 && (
            <div className="flex gap-4">
              {productImages.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => useStateActiveImage(idx)}
                  className={`w-20 h-20 bg-white rounded-lg border flex items-center justify-center p-2 flex-shrink-0 transition-colors ${
                    activeImage === idx
                      ? "border-green-600 ring-1 ring-green-600 shadow-sm"
                      : "border-slate-200 hover:border-green-400"
                  }`}
                >
                  <img
                    src={img}
                    alt={`Náhled ${idx + 1}`}
                    className="max-h-full max-w-full object-contain mix-blend-multiply"
                  />
                </button>
              ))}
            </div>
          )}

          {/* DLOUHÝ TEXT O PRODUKTU */}
          {product.about && (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
              <div className="p-6 md:p-8">
                <h3 className="text-xl font-bold text-[#2a3b4c] mb-6 border-b border-slate-100 pb-4">
                  O produktu
                </h3>
                <div
                  className="text-slate-600 leading-relaxed space-y-4 text-[15px]"
                  dangerouslySetInnerHTML={{ __html: product.about }}
                />
              </div>
            </div>
          )}

          {/* 📱 SPECIÁLNÍ BOX PRO APLIKACI (Zobrazí se pouze u displeje EKD01) */}
          {isEKD01 && (
            <div className="bg-gradient-to-br from-slate-50 to-blue-50 border border-blue-100 rounded-xl p-6 md:p-8 shadow-sm">
              <h3 className="text-xl font-bold text-[#2a3b4c] mb-3 flex items-center gap-2">
                📱 Aplikace zdarma ke stažení
              </h3>
              <p className="text-[15px] text-slate-600 mb-6 leading-relaxed">
                Tento displej je vybaven <strong>Bluetooth</strong> modulem. Stáhněte si bezplatnou aplikaci <strong>BikeGo</strong>, spárujte displej s vaším mobilním telefonem a získejte přístup k navigaci, detailním statistikám jízdy a pokročilému nastavení vašeho elektrokola.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="https://apps.apple.com/cz/app/bikego/id1615694432?l=cs"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 bg-black text-white px-5 py-3 rounded-xl font-bold hover:bg-slate-800 transition flex-1 shadow-sm"
                >
                  🍏 App Store (iOS)
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=com.huiye.ebike&hl=cs"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 bg-[#16a34a] text-white px-5 py-3 rounded-xl font-bold hover:bg-green-700 transition flex-1 shadow-sm"
                >
                  🤖 Google Play
                </a>
              </div>
            </div>
          )}

          {/* UPOZORNĚNÍ K LEGALITĚ */}
          {product.legalInfo && product.type !== "display" && (
            <div className={`rounded-xl border overflow-hidden shadow-sm ${
              product.legalInfo.type === "green" ? "border-green-300" : "border-red-300"
            }`}>
              {/* Barevná hlavička */}
              <div className={`px-5 py-3 flex items-center gap-3 border-b ${
                product.legalInfo.type === "green" ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"
              }`}>
                {product.legalInfo.type === "green" ? (
                  <ShieldCheck className="w-5 h-5 text-green-700 flex-shrink-0" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-red-700 flex-shrink-0" />
                )}
                <h3 className={`text-sm md:text-base font-black uppercase tracking-wide ${
                  product.legalInfo.type === "green" ? "text-green-800" : "text-red-800"
                }`}>
                  {product.legalInfo.title}
                </h3>
              </div>
              
              {/* Bílý spodek s textem z Firebase */}
              <div className="bg-white p-5 md:p-6">
                <div 
                  className={`text-[14px] md:text-[15px] leading-relaxed space-y-3 ${
                    product.legalInfo.type === "green" ? "text-slate-700" : "text-red-700 font-medium"
                  }`}
                  dangerouslySetInnerHTML={{ __html: product.legalInfo.text }}
                />
              </div>
            </div>
          )}

          {/* OBSAH BALENÍ */}
          {product.packageContents && product.packageContents.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
              <button
                onClick={() => setIsPackageOpen(!isPackageOpen)}
                className="w-full p-6 flex justify-between items-center bg-white hover:bg-slate-50 transition cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <PackageCheck className="w-6 h-6 text-green-600" />
                  <h3 className="text-lg font-bold text-[#2a3b4c]">
                    Co najdete v balení?
                  </h3>
                </div>
                <ChevronRight
                  className={`w-5 h-5 text-slate-400 transition-transform duration-300 ${
                    isPackageOpen ? "rotate-90" : ""
                  }`}
                />
              </button>
              {isPackageOpen && (
                <div className="px-6 pb-6 animate-in slide-in-from-top-2 fade-in duration-200">
                  {product.packageContents.map((item, i) => (
                    <div
                      key={i}
                      className="flex flex-col sm:flex-row gap-4 items-start sm:items-center py-5 border-t border-slate-100"
                    >
                      <div className="w-20 h-20 bg-white rounded-xl flex items-center justify-center p-2 flex-shrink-0 border border-slate-100 shadow-sm">
                        <img
                          src={item.img}
                          alt={item.name}
                          className="max-h-full max-w-full object-contain mix-blend-multiply"
                          onError={(e) => (e.target.style.display = "none")}
                        />
                      </div>
                      <div>
                        <h4 className="font-bold text-[#2a3b4c] text-[15px] mb-1">
                          {item.name}
                        </h4>
                        <p className="text-sm text-slate-500 leading-relaxed">
                          {item.desc}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

        </div>

        {/* PRAVÝ SLOUPEC: Název, parametry, cena a tlačítka */}
        <div className="lg:w-7/12 flex flex-col">
          <h1 className="text-3xl md:text-4xl font-bold text-[#2a3b4c] mb-6">
            {product.name}
          </h1>

          {/* Technické specifikace */}
          <div className="border border-slate-200 rounded-xl overflow-hidden mb-8 shadow-sm">
            <div className="bg-slate-50 p-4 font-bold text-[#2a3b4c] border-b border-slate-200">
              Technické specifikace
            </div>
            <div className="bg-white flex flex-col">
              {product.specsTable && product.specsTable.length > 0 ? (
                product.specsTable.map((spec, i) => (
                  <div
                    key={i}
                    className="flex justify-between p-4 border-b border-slate-100 last:border-0 text-sm even:bg-slate-50/50"
                  >
                    <span className="text-[#2a3b4c] font-medium">
                      {spec.label}
                    </span>
                    <span className="text-slate-600 font-medium">{spec.value}</span>
                  </div>
                ))
              ) : (
                <div className="p-4 text-sm text-slate-400 italic">Technické parametry nejsou k dispozici.</div>
              )}
            </div>
          </div>

          {/* Cena a Tlačítka */}
          <div className="text-4xl font-extrabold text-green-600 mb-6">
            {product.price === 0
              ? "Zdarma"
              : `${Number(product.price).toLocaleString()} Kč`}
          </div>

          <div className="flex flex-col sm:flex-row gap-4 mb-10">
            <button
              onClick={() =>
                addToCart({
                  type: "single",
                  product: product,
                  price: product.price,
                })
              }
              className="px-8 py-3.5 bg-green-600 hover:bg-green-700 text-white rounded-lg font-bold flex items-center justify-center gap-2 w-full sm:w-auto transition shadow-sm"
            >
              <ShoppingCart className="w-5 h-5" /> Přidat do košíku
            </button>
            <button
              onClick={() => toggleFavorite && toggleFavorite(product.id)}
              className="px-8 py-3.5 bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-lg font-bold flex items-center justify-center gap-2 w-full sm:w-auto transition shadow-sm"
            >
              <Heart
                className={`w-5 h-5 ${
                  isFav ? "fill-red-500 text-red-500" : "text-slate-400"
                }`}
              />{" "}
              {isFav ? "Odebrat z oblíbených" : "Přidat do oblíbených"}
            </button>
          </div>

          {/* TIPY PRO ŽIVOTNOST */}
          {product.tips && (
            <div className="bg-[#fff9e6] border border-[#f3e1a5] rounded-xl p-6 md:p-8 flex gap-4 items-start shadow-sm">
              <AlertCircle className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-bold text-[#2a3b4c] mb-3">
                  Tip pro maximální životnost:
                </h3>
                <div
                  className="text-slate-600 text-[15px] leading-relaxed space-y-2"
                  dangerouslySetInnerHTML={{ __html: product.tips }}
                />
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;